const SUPPORT_FORM_BASE_URL =
  "https://support.freshpaint.io/hc/en-us/requests/new";

type SupportFormParams = {
  /** Pre-fills the requester's email so they don't have to retype it. */
  email: string;
  /**
   * Pre-formatted `<strong>Label:</strong> value` lines, grouped under headings,
   * that give support the context to identify the account without a round trip.
   */
  sections: Array<{ heading: string; lines: string[] }>;
};

/**
 * Builds a URL to Freshpaint's Zendesk support form with the requester's email
 * and a debugging-context description pre-filled.
 *
 * The query parameters below belong to Zendesk's support form page.
 *
 * @see https://support.zendesk.com/hc/en-us/articles/4408839114522-Creating-pre-filled-ticket-forms
 * @see https://support.zendesk.com/hc/en-us/articles/4408886739098-About-ticket-fields
 */
export function buildSupportFormURL({
  email,
  sections,
}: SupportFormParams): URL {
  const body = sections
    .map(
      ({ heading, lines }) =>
        `<h4>${heading}</h4>\n\t${lines.join("\t<br />")}`,
    )
    .join("\n");

  const description = `
Please describe your issue here:<br /><br /><br /><br /><br />
<em>The debugging information below will help the Freshpaint support team more quickly resolve your issue:</em>
${body}
`;

  const queryParams = new URLSearchParams({
    tf_anonymous_requester_email: email,
    tf_description: description,
  });

  return new URL(`${SUPPORT_FORM_BASE_URL}?${queryParams.toString()}`);
}

/** The support form with nothing pre-filled, for callers with no user context. */
export function bareSupportFormURL(): URL {
  return new URL(SUPPORT_FORM_BASE_URL);
}

/**
 * The page and browser context lines shared by every support link. Kept
 * separate from account context because it's available even before (or without)
 * an account.
 */
export function pageDetailLines(): string[] {
  return [
    `<strong>Page URL:</strong> ${window.location.href}`,
    `<strong>User Agent:</strong> ${navigator.userAgent}`,
    `<strong>Referrer:</strong> ${document.referrer || "(none)"}`,
  ];
}
